/** @file Draw-order fixes of a mirrorFix direction: the op list (remove one) and an add row. */
import { drawOrderOpSlots } from '@pose2d/format/doc/drawOrderOpSlots';
import type { Doc, DrawOrderOp } from '@pose2d/format/types';
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import { useState } from 'react';
import { Button } from '../../ui-kit/Button';
import { Select } from '../../ui-kit/Select';

/** Props of FixOps; `onChange` receives the whole new list (setFix replaces it). */
export interface FixOpsProps {
  doc: Doc;
  t: Translate;
  ops: readonly DrawOrderOp[];
  onChange: (ops: DrawOrderOp[]) => void;
}

const KINDS: Record<DrawOrderOp['op'], CatalogKey> = { swap: 'panels.directions.swap', moveAbove: 'panels.directions.moveAbove', moveBelow: 'panels.directions.moveBelow' };

/** The op built from the add row: swap a/b, or move `a` above/below `b`. */
const opOf = (op: DrawOrderOp['op'], a: string, b: string): DrawOrderOp => (op === 'swap' ? { op, a, b } : { op, slot: a, ref: b });

/** Ops apply in list order after any draw order, every frame (§5.8). */
export function FixOps({ doc, t, ops, onChange }: FixOpsProps) {
  const slots = doc.drawOrder.map((id) => ({ value: id, label: doc.slots[id]?.name ?? id }));
  const [kind, setKind] = useState<DrawOrderOp['op']>('swap');
  const [a, setA] = useState(doc.drawOrder[0] ?? '');
  const [b, setB] = useState(doc.drawOrder[1] ?? '');
  const name = (id: string): string => doc.slots[id]?.name ?? id;
  const kinds = Object.entries(KINDS).map(([value, key]) => ({ value, label: t(key) }));
  return (
    <fieldset className="rig-fields">
      <legend>{t('panels.directions.fixOps')}</legend>
      <ul aria-label={t('panels.directions.fixOps')}>
        {ops.map((op, i) => {
          const [x, y] = drawOrderOpSlots(op);
          return (
            <li key={`${String(i)}-${op.op}`} className="rig-row">
              {t('panels.directions.opRow', { op: t(KINDS[op.op]), a: name(x), b: name(y) })}
              <Button label={t('panels.rig.delete')} danger onClick={() => { onChange(ops.filter((_, j) => j !== i)); }} />
            </li>
          );
        })}
      </ul>
      <div className="rig-row">
        <Select label={t('panels.directions.fixOps')} value={kind} options={kinds} onChange={(v) => { setKind(v === 'moveAbove' || v === 'moveBelow' ? v : 'swap'); }} />
        <Select label={t('panels.skins.slot')} value={a} options={slots} onChange={setA} />
        <Select label={t('panels.skins.slot')} value={b} options={slots} onChange={setB} />
        <Button label={t('panels.directions.addOp')} disabled={a === '' || b === '' || a === b} onClick={() => { onChange([...ops, opOf(kind, a, b)]); }} />
      </div>
    </fieldset>
  );
}
